import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';
import { LegalDocumentViewer } from '@/components/LegalDocumentViewer';
import { LEGAL_DOCUMENTS } from '@/legal';

const STORAGE_KEY = 'cookie_notice_ack_v1';

/**
 * Плашка про cookie и локальное хранилище.
 *
 * Показывается ТОЛЬКО в веб-версии: внутри Telegram Mini App хранилищем распоряжается
 * само приложение Telegram, и плашка там была бы и неуместной, и вводящей в заблуждение.
 *
 * Формулировки намеренно без «мы ценим вашу приватность»: отслеживающих cookie в сервисе
 * нет вовсе (ни счётчиков, ни пикселей), поэтому спрашивать разрешение не на что —
 * человека нужно проинформировать, а не выпрашивать у него согласие на то, чего мы не делаем.
 * Если аналитика появится — здесь должен появиться настоящий выбор, а не одна кнопка.
 */
export function CookieNotice() {
  const [visible, setVisible] = useState(false);
  const [docOpen, setDocOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const inTelegram = !!(window as any).Telegram?.WebApp?.initData;
    if (inTelegram) return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return; // хранилище недоступно — молчим, чтобы не показывать плашку каждый раз
    }
    setVisible(true);
  }, []);

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      /* не критично */
    }
    setVisible(false);
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 28 }}
            className="fixed inset-x-0 bottom-0 z-[70] p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            role="region"
            aria-label="Уведомление об использовании cookie"
          >
            <div className="mx-auto max-w-md rounded-2xl bg-bg-card border border-line p-4 shadow-2xl">
              <div className="flex gap-3">
                <Cookie className="w-5 h-5 text-emerald-glow shrink-0 mt-0.5" aria-hidden />
                <div className="min-w-0">
                  <p className="text-sm text-ink leading-relaxed">
                    Мы сохраняем в браузере только техническое: вход в аккаунт и отметки
                    о просмотренных подсказках.{' '}
                    <span className="text-ink-muted">
                      Счётчиков, аналитики и рекламных пикселей у нас нет.
                    </span>
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={accept}
                      className="px-4 py-2 rounded-xl bg-emerald-glow text-bg font-semibold text-sm active:scale-95 transition"
                    >
                      Понятно
                    </button>
                    <button
                      onClick={() => setDocOpen(true)}
                      className="px-3 py-2 rounded-xl text-sm text-ink-muted hover:text-ink transition"
                    >
                      Подробнее
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <LegalDocumentViewer
        document={docOpen ? LEGAL_DOCUMENTS['cookies'] : null}
        onClose={() => setDocOpen(false)}
      />
    </>
  );
}
